import React, { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { addFace, deleteFace, listFaces } from '../../services/api';

const videoConstraints = { width: 480, height: 360, facingMode: 'user' };

export default function UsersTab({ config }) {
  const hasAddWebhook = !!config.addFaceWebhookUrl;
  const hasDeleteWebhook = !!config.deleteFaceWebhookUrl;
  const hasListWebhook = !!config.listFacesWebhookUrl;

  // Añadir usuario
  const webcamRef = useRef(null);
  const [capturedImg, setCapturedImg] = useState(null);
  const [nombre, setNombre] = useState('');
  const [patientId, setPatientId] = useState('');
  const [addStatus, setAddStatus] = useState(null); // { type: 'ok'|'error', msg }
  const [addLoading, setAddLoading] = useState(false);

  // Listar / eliminar
  const [faces, setFaces] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const capture = useCallback(() => {
    const img = webcamRef.current?.getScreenshot();
    if (img) setCapturedImg(img);
  }, []);

  const handleAdd = async () => {
    if (!capturedImg) return setAddStatus({ type: 'error', msg: 'Primero captura una foto.' });
    if (!nombre.trim()) return setAddStatus({ type: 'error', msg: 'El nombre es obligatorio.' });
    if (!patientId.trim()) return setAddStatus({ type: 'error', msg: 'El ID de paciente es obligatorio.' });

    setAddLoading(true);
    setAddStatus(null);
    try {
      const res = await addFace(capturedImg, patientId.trim(), nombre.trim(), config.addFaceWebhookUrl);
      if (res.success) {
        setAddStatus({ type: 'ok', msg: `Usuario añadido correctamente. FaceId: ${res.faceId}` });
        setCapturedImg(null);
        setNombre('');
        setPatientId('');
      } else {
        setAddStatus({ type: 'error', msg: res.message || 'Error al añadir el usuario.' });
      }
    } catch {
      setAddStatus({ type: 'error', msg: 'Error de conexión con el webhook.' });
    } finally {
      setAddLoading(false);
    }
  };

  const handleList = async () => {
    setListLoading(true);
    setListError('');
    try {
      const res = await listFaces(config.listFacesWebhookUrl);
      setFaces(res.faces || []);
    } catch {
      setListError('Error al cargar los usuarios.');
    } finally {
      setListLoading(false);
    }
  };

  const handleDelete = async (faceId, patientId) => {
    if (!window.confirm(`¿Seguro que quieres eliminar a "${patientId}" de Rekognition y del Sheet?`)) return;
    setDeletingId(faceId);
    try {
      await deleteFace(faceId, patientId, config.deleteFaceWebhookUrl);
      setFaces(prev => prev.filter(f => f.faceId !== faceId));
    } catch {
      alert('Error al eliminar el usuario.');
    } finally {
      setDeletingId(null);
    }
  };

  const webhooksMissing = !hasAddWebhook || !hasDeleteWebhook || !hasListWebhook;

  if (webhooksMissing) {
    return (
      <div className="activities-notice">
        <strong>Webhooks no configurados</strong>
        Para gestionar usuarios ve a <em>Configuración → Gestión de usuarios</em> y añade las URLs de los tres webhooks de n8n (añadir, eliminar, listar).
      </div>
    );
  }

  return (
    <div>
      {/* Añadir usuario */}
      <p className="config-section-title">Añadir usuario</p>

      <div className="users-webcam-container">
        {!capturedImg ? (
          <>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className="users-webcam"
            />
            <button className="users-capture-btn" onClick={capture}>📸 Capturar foto</button>
          </>
        ) : (
          <>
            <img src={capturedImg} alt="Captura" className="users-preview" />
            <button className="users-retake-btn" onClick={() => setCapturedImg(null)}>↩ Repetir</button>
          </>
        )}
      </div>

      <div className="config-field" style={{ marginTop: '1rem' }}>
        <label>Nombre del paciente</label>
        <input type="text" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Joan García" />
      </div>
      <div className="config-field">
        <label>ID del paciente</label>
        <p className="field-hint">Debe coincidir con el PatientID del Google Sheets.</p>
        <input type="text" value={patientId} onChange={e => setPatientId(e.target.value)} placeholder="Ej: PAT001" />
      </div>

      <button className="config-save-btn" onClick={handleAdd} disabled={addLoading}>
        {addLoading ? 'Guardando...' : 'Guardar usuario'}
      </button>

      {addStatus && (
        <p className={addStatus.type === 'ok' ? 'config-saved-msg' : 'users-error-msg'}>
          {addStatus.type === 'ok' ? '✓ ' : '✗ '}{addStatus.msg}
        </p>
      )}

      {/* Listar y eliminar */}
      <p className="config-section-title" style={{ marginTop: '2rem' }}>Usuarios registrados</p>

      <button className="users-load-btn" onClick={handleList} disabled={listLoading}>
        {listLoading ? 'Cargando...' : '🔄 Cargar usuarios'}
      </button>

      {listError && <p className="users-error-msg">{listError}</p>}

      {faces !== null && (
        faces.length === 0 ? (
          <p style={{ color: '#a0aec0', marginTop: '0.75rem' }}>No hay usuarios registrados.</p>
        ) : (
          <div className="users-list">
            {faces.map(f => (
              <div key={f.faceId} className="users-list-item">
                <div>
                  <span className="users-patient-id">{f.externalImageId || '—'}</span>
                  <span className="users-face-id">{f.faceId.substring(0, 8)}...</span>
                </div>
                <button
                  className="users-delete-btn"
                  onClick={() => handleDelete(f.faceId, f.externalImageId)}
                  disabled={deletingId === f.faceId}
                >
                  {deletingId === f.faceId ? '...' : '🗑️'}
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
